import { motion, useMotionValue, useTransform } from 'motion/react';

import { cn } from '@/lib/utils';

import Button from '@/decodo/components/atoms/Button';

type MenuCarouselPaginationProps = {
  itemCount: number;
  activeIndex: number;
  handlePageChange: (index: number) => void;
};

export function MenuCarouselPagination({
  itemCount,
  activeIndex,
  handlePageChange,
}: MenuCarouselPaginationProps) {
  const x = useMotionValue(0);
  const scale = useTransform(
    x,
    Array.from(Array(2 * itemCount - 1).keys()).map((i) => i * 8),
    Array.from(Array(2 * itemCount - 1).keys()).map((i) =>
      i % 2 === 0 ? 1 : 0.5
    )
  );

  return (
    <ul
      className='relative flex gap-x-2'
      role='group'
      aria-label='Carousel pagination'
    >
      {Array.from(Array(itemCount).keys()).map((_, i) => (
        <li key={i} className='leading-[0]'>
          <Button
            noStyles
            onClick={() => handlePageChange(i)}
            className={cn(
              'bg-gray-dark-200 relative h-2 w-2 outline-none duration-300 after:absolute after:-inset-1.5 focus-visible:ring-1 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
              activeIndex !== i && 'hover-hover:hover:bg-gray-dark-400 z-[1]'
            )}
          >
            <span className='sr-only'>
              Slide {i + 1} {activeIndex === i && '(Current item)'}
            </span>
          </Button>
        </li>
      ))}
      <div className='sr-only' aria-live='polite' aria-atomic='true'>
        Slide {activeIndex + 1} of {itemCount}
      </div>
      <motion.div
        aria-hidden
        className='bg-gray-dark-950 absolute z-[1] h-2 w-2'
        style={{ scale }}
        animate={{ x: activeIndex * 2 * 8 }}
        transition={{ ease: 'easeOut', duration: 0.2 }}
        onUpdate={(latest) => {
          if (typeof latest.x !== 'number') return;
          x.set(latest.x);
        }}
      />
    </ul>
  );
}
